import { useState, useCallback, useMemo } from 'react'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { useOutletContext } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    ORG_MEMBER_ROLE_ADMIN,
    ORG_MEMBER_ROLE_MEMBER,
    ORG_MEMBER_ROLE_OWNER,
} from '@constants'

const useMembersViewModel = () => {
    
    const [memberQuery, setMemberQuery] = useState('')
    const [loadingAddMember, setLoadingAddMember] = useState(false)
    const [loadingInviteMember, setLoadingInviteMember] = useState(false)
    const [newMemberEmail, setNewMemberEmail] = useState('')
    const [inviteMemberFirstName, setInviteMemberFirstName] = useState('')
    const [inviteMemberLastName, setInviteMemberLastName] = useState('')
    const [inviteMemberEmail, setInviteMemberEmail] = useState('')
    const [pendingRemoveMember, setPendingRemoveMember] = useState(null)
    const [confirmChangeOwnerModalOpen, setConfirmChangeOwnerModalOpen] = useState(false)
    const [confirmChangeOwnerModalData, setConfirmChangeOwnerModalData] = useState(null)
    
    const {
        organization,
        organizationError,
        organizationIsLoading,
        fetchOrganization,
    } = useOutletContext()
    
    const user = useWireValue(store.user)
    const [addExistingOrgMemberDialogOpen, setAddExistingOrgMemberDialogOpen] =
        useWireState(store.addExistingOrgMemberDialogOpen)
    const [inviteNewOrgMemberDialogOpen, setInviteNewOrgMemberDialogOpen] =
        useWireState(store.inviteNewOrgMemberDialogOpen)
    
    // Sort owners first
    const sortedMembers = useMemo(() => {
        
        if (!organization?.members) return []
        
        const roleOrder = [
            ORG_MEMBER_ROLE_OWNER,
            ORG_MEMBER_ROLE_ADMIN,
            ORG_MEMBER_ROLE_MEMBER,
        ]
        
        return [...organization.members].sort((a, b) => {
            
            const indexA = roleOrder.indexOf(a.UserOrganizationJoin?.memberRole)
            const indexB = roleOrder.indexOf(b.UserOrganizationJoin?.memberRole)
            
            if (indexA > indexB) return 1
            if (indexA < indexB) return -1
            
            if (a.createdAt > b.createdAt) return 1
            if (a.createdAt < b.createdAt) return -1
            
            return 0
            
        })
        
    }, [organization])
    
    const filteredMembers = useMemo(() => {
        
        if (!memberQuery?.length)
            return sortedMembers
        
        return sortedMembers.filter(it => (
            it.email.toLowerCase().includes(memberQuery) ||
            (`${it.firstName ?? ''} ${it.lastName ?? ''}`).toLowerCase().includes(memberQuery)
        ))
        
    }, [memberQuery, sortedMembers])
    
    const currentUserOrgJoin = useMemo(() => (
        user?.organizations
            ?.find(it => it.id === organization?.id)
            ?.UserOrganizationJoin
    ), [user, organization])
    
    const onAddMemberClick = useCallback(async () => {
        
        setLoadingAddMember(true)
        
        try {
            await actions.addMemberToOrg(organization.id, newMemberEmail)
            await actions.fetchOrgById(organization.id, true)
            setNewMemberEmail('')
            toast.success('Added new member')
        } catch (e) {
            console.error('onAddMemberClick', e)
            
            if (e.response && e.response.status === 409) {
                toast.error('Already a member of this organization')
            } else {
                toast.error('Failed to add new member')
            }
        }
        
        setLoadingAddMember(false)
        
    }, [organization, newMemberEmail])
    
    const onInviteMemberClick = useCallback(async () => {
        
        setLoadingInviteMember(true)
        
        try {
            await actions.inviteMemberToOrg(
                organization.id,
                inviteMemberFirstName,
                inviteMemberLastName,
                inviteMemberEmail,
            )
            await actions.fetchOrgById(organization.id, true)
            setInviteMemberFirstName('')
            setInviteMemberLastName('')
            setInviteMemberEmail('')
            toast.success('Invited new member')
        } catch (e) {
            console.error('onInviteMemberClick', e)
            
            if (e.response && e.response.status === 409) {
                toast.error('Already a member of this organization')
            } else {
                toast.error('Failed to invite new member')
            }
        }
        
        setLoadingInviteMember(false)
        
    }, [organization, inviteMemberFirstName, inviteMemberLastName, inviteMemberEmail])
    
    const onConfirmRemoveMemberClick = useCallback(async () => {
        
        try {
            await actions.removeMemberFromOrg(organization.id, pendingRemoveMember.id)
            await actions.fetchOrgById(organization.id, true)
            toast.success(`Removed member "${pendingRemoveMember.email}"`)
        } catch (e) {
            console.error('onConfirmRemoveMemberClick', e)
            toast.error('Failed to remove member')
        }
        
    }, [organization, pendingRemoveMember])
    
    const changeMemberRole = useCallback(async (memberId, role) => {
        
        try {
            await actions.updateMemberInOrganization(organization.id, memberId, role)
            await actions.fetchProfile('organizations')
            await actions.fetchOrgById(organization.id)
        } catch (e) {
            console.error('changeMemberRole', e)
            toast.error('Failed to update member role')
        }
        
    }, [organization])
    
    const completeChangeMemberRole = useCallback(async () => {
        
        const { memberId, role } = confirmChangeOwnerModalData
        
        await changeMemberRole(memberId, role)
        
        setConfirmChangeOwnerModalData(null)
        
    }, [confirmChangeOwnerModalData, changeMemberRole])
    
    const onChangeMemberRole = useCallback(memberId => async e => {
        
        const role = e.target.value
        const member = organization.members.find(it => it.id === memberId)
        const userOrgJoin = organization.members
            .find(it => it.id === user.id)
            .UserOrganizationJoin
        const userIsOwner = userOrgJoin?.memberRole === ORG_MEMBER_ROLE_OWNER
        const memberIsOwner = member.UserOrganizationJoin?.memberRole === ORG_MEMBER_ROLE_OWNER
        
        if ((role === ORG_MEMBER_ROLE_OWNER && !userIsOwner) || memberIsOwner)
            return toast.warn('Only owners can change their own role')
        
        if (memberIsOwner)
            return toast.warn('You must choose another member as owner')
        
        // To change owner, we need to transfer the
        // organization, so show a confirmation modal first
        if (role === ORG_MEMBER_ROLE_OWNER) {
            
            const member = organization.members.find(it => it.id === memberId)
            
            setConfirmChangeOwnerModalData({ memberId, role, memberEmail: member.email })
            setConfirmChangeOwnerModalOpen(true)
            return
            
        }
        
        await changeMemberRole(memberId, role)
        
    }, [user, organization])
    
    return {
        
        // State
        memberQuery,
        setMemberQuery,
        loadingAddMember,
        loadingInviteMember,
        
        newMemberEmail,
        setNewMemberEmail,
        
        inviteMemberFirstName,
        setInviteMemberFirstName,
        inviteMemberLastName,
        setInviteMemberLastName,
        inviteMemberEmail,
        setInviteMemberEmail,
        
        pendingRemoveMember,
        setPendingRemoveMember,
        
        confirmChangeOwnerModalOpen,
        setConfirmChangeOwnerModalOpen,
        confirmChangeOwnerModalData,
        setConfirmChangeOwnerModalData,
        
        // Global State
        user,
        organization,
        organizationError,
        organizationIsLoading,
        fetchOrganization,
        addExistingOrgMemberDialogOpen,
        setAddExistingOrgMemberDialogOpen,
        inviteNewOrgMemberDialogOpen,
        setInviteNewOrgMemberDialogOpen,
        
        // Memos
        sortedMembers,
        filteredMembers,
        currentUserOrgJoin,
        
        // Methods
        onAddMemberClick,
        onInviteMemberClick,
        onConfirmRemoveMemberClick,
        completeChangeMemberRole,
        onChangeMemberRole,
        
    }
    
}

export default useMembersViewModel
